import { format } from 'date-fns';

/** Listings are South East Queensland. Brisbane does not observe daylight saving. */
export const BRISBANE_TIME_ZONE = 'Australia/Brisbane';

/**
 * Angular's DatePipe only accepts a fixed offset (`+1000`), not an IANA name.
 * Australia/Brisbane is UTC+10 all year, so this offset matches {@link BRISBANE_TIME_ZONE}.
 */
export const BRISBANE_DATE_PIPE_TIMEZONE = '+1000';

function partValue(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  return Number(parts.find(part => part.type === type)?.value ?? '0');
}

/**
 * A Date whose local calendar fields match Australia/Brisbane wall time for `instant`.
 * date-fns formats those local fields, so SSR in UTC and a browser in any zone print the same clock time.
 */
export function brisbaneWallClockDate(instant: Date): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BRISBANE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);

  let year = partValue(parts, 'year');
  let month = partValue(parts, 'month');
  let day = partValue(parts, 'day');
  let hour = partValue(parts, 'hour');
  if (hour === 24) {
    hour = 0;
    const rolled = new Date(Date.UTC(year, month - 1, day));
    rolled.setUTCDate(rolled.getUTCDate() + 1);
    year = rolled.getUTCFullYear();
    month = rolled.getUTCMonth() + 1;
    day = rolled.getUTCDate();
  }

  return new Date(year, month - 1, day, hour, partValue(parts, 'minute'), partValue(parts, 'second'));
}

export function formatInBrisbane(instant: Date, pattern: string): string {
  return format(brisbaneWallClockDate(instant), pattern);
}

/** Today's civil date in Brisbane, stored as local Y-M-D so the datepicker does not follow the host zone. */
export function brisbaneToday(instant = new Date()): Date {
  const wall = brisbaneWallClockDate(instant);
  return new Date(wall.getFullYear(), wall.getMonth(), wall.getDate());
}

export function formatCivilDate(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatCivilTime(date: Date): string {
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** `yyyy-MM-dd` month starts from Brisbane's current month, for the location calendar query. */
export function brisbaneMonthParam(count: number, instant = new Date()): string {
  const today = brisbaneToday(instant);
  return Array.from({ length: count }, (_, index) => {
    const month = new Date(today.getFullYear(), today.getMonth() + index, 1);
    return formatCivilDate(month);
  }).join(',');
}
